// ===== Enums =====

export enum IntegrationType {
  None = 'None',
  Credit = 'Credit',
  Underwrite = 'Underwrite',
  PropertyData = 'PropertyData',
  Los = 'Los',
  Pricing = 'Pricing',
  Email = 'Email',
  Flood = 'Flood',
  IncomeEmployment = 'IncomeEmployment',
  Asset = 'Asset',
  Fees = 'Fees',
}

export enum Operator {
  Not = 'Not',
  And = 'And',
  Or = 'Or',
  IsOneOf = 'IsOneOf',
  StartsWith = 'StartsWith',
  Contains = 'Contains',
  ContainsAny = 'ContainsAny',
  ContainsAll = 'ContainsAll',
  EndsWith = 'EndsWith',
  IsEmpty = 'IsEmpty',
  Multiply = 'Multiply',
  Divide = 'Divide',
  Mod = 'Mod',
  Add = 'Add',
  Subtract = 'Subtract',
  LessThan = 'LessThan',
  LessThanOrEqual = 'LessThanOrEqual',
  GreaterThan = 'GreaterThan',
  GreaterThanOrEqual = 'GreaterThanOrEqual',
  Equal = 'Equal',
  NotEqual = 'NotEqual',
  Regex = 'Regex',
  DateAdd = 'DateAdd',
  DateInRange = 'DateInRange',
  FirstValue = 'FirstValue',
  IfElse = 'IfElse',
  WhenElse = 'WhenElse',
  IfEmpty = 'IfEmpty',
}

export enum LogicalOperator {
  And = 'And',
  Or = 'Or',
}

export enum WaterfallRuleType {
  StepCondition = 'StepCondition',
  ExitCondition = 'ExitCondition',
  RequestSetting = 'RequestSetting',
}

export enum WaterfallStepAction {
  Continue = 'Continue',
  Abort = 'Abort',
}

export enum WaterfallExecutionMode {
  StepEvaluation = 'StepEvaluation',
  AI = 'AI',
}

export enum WaterfallExecutionStatus {
  Running = 'Running',
  Complete = 'Complete',
  Error = 'Error',
  Abort = 'Abort',
}

export enum DataType {
  Text = 'Text',
  Number = 'Number',
  Integer = 'Integer',
  Enumeration = 'Enumeration',
  Date = 'Date',
  DateTime = 'DateTime',
  TrueFalse = 'TrueFalse',
}

export enum MetadataContextType {
  FeeInitial = 'FeeInitial',
  FeeRequestValue = 'FeeRequestValue',
  FeeFinal = 'FeeFinal',
  FeeIncludeFee = 'FeeIncludeFee',
  FeeRequestValueTarget = 'FeeRequestValueTarget',
  WaterfallRequestValueTarget = 'WaterfallRequestValueTarget',
  WaterfallContext = 'WaterfallContext',
  WaterfallRequestValue = 'WaterfallRequestValue',
}

export enum OperandType {
  Unary = 'Unary',
  Binary = 'Binary',
}

// ===== Expression Models =====

export interface SimpleBinaryExpression {
  left?: string | null;
  operator: Operator;
  right?: string[] | null;
}

export interface ExpressionItem {
  expression?: SimpleBinaryExpression | null;
  expressionGroup?: SimpleExpression | null;
}

export interface SimpleExpression {
  expressions?: ExpressionItem[] | null;
  logicalOperator: LogicalOperator;
  isNot?: boolean;
}

export interface FieldMetadata {
  fieldName: string | null;
  dataType: DataType;
  isList: boolean;
  enumType: string | null;
}

export interface OperatorInfo {
  operator: string;
  name: string | null;
  symbol: string | null;
  operandType: OperandType;
  isLogical: boolean;
  supportedDataTypes: DataType[];
}

export interface EnumValueData {
  name: string | null;
  displayName: string | null;
  isHidden: boolean;
  order: number;
  value: any;
}

// ===== Waterfall Rule Models =====

export interface OverrideSettingViewModel {
  fieldName: string | null;
  value: string | null;
}

export interface WaterfallRuleViewModel {
  expression?: SimpleExpression | null;
  ruleType: WaterfallRuleType;
  overrideSetting?: OverrideSettingViewModel | null;
}

// ===== Step Model =====

export interface StepViewModel {
  description?: string | null;
  stepGuid: string;
  nextSteps?: StepViewModel[] | null;
  integrationSettingsId: number;
  waterfallRules?: WaterfallRuleViewModel[] | null;
  stepTimeoutInSeconds?: number | null;
  onTimeoutExceededAction?: WaterfallStepAction;
  onFatalErrorAction?: WaterfallStepAction;
}

// ===== Waterfall Version Models =====

export interface WaterfallVersionViewModel {
  tenantId: string;
  createdDate: string;
  createdBy: string;
  updatedDate?: string | null;
  updatedBy?: string | null;
  waterfallVersionId: number;
  waterfallId: number;
  versionNumber: number;
  executionMode: WaterfallExecutionMode;
  initialStep: StepViewModel;
}

export interface CreateOrUpdateWaterfallVersionViewModel {
  waterfallVersionId?: number | null;
  waterfallId?: number;
  executionMode: WaterfallExecutionMode;
  initialStep: StepViewModel;
}

// ===== Waterfall Models =====

export interface WaterfallViewModel {
  tenantId: string;
  createdDate: string;
  createdBy: string;
  updatedDate?: string | null;
  updatedBy?: string | null;
  waterfallId: number;
  name: string;
  integrationType: IntegrationType;
  currentVersionId?: number | null;
  lastExecuted?: string | null;
  disabled: boolean;
  archived: boolean;
  executionOrder: number;
  currentVersion?: WaterfallVersionViewModel | null;
  versions?: WaterfallVersionViewModel[] | null;
}

export interface CreateOrUpdateWaterfallViewModel {
  waterfallId?: number | null;
  name: string;
  integrationType: IntegrationType;
  currentVersionId?: number | null;
  disabled?: boolean;
  archived?: boolean;
}

// ===== Trace/Execution Models =====

export interface WaterfallTraceRecordViewModel {
  tenantId: string;
  createdDate: string;
  createdBy: string;
  updatedDate?: string | null;
  updatedBy?: string | null;
  waterfallExecutionRecordId: number;
  waterfallId: number;
  waterfallVersionId: number;
  dateStarted: string;
  status: WaterfallExecutionStatus;
  waterfallVersion: WaterfallVersionViewModel;
}

// ===== Form/UI Models =====

export interface WaterfallFormModel {
  name: string;
  integrationType: IntegrationType;
  selectedVersion?: number | null;
}

/** @deprecated Legacy DTO - use WaterfallViewModel instead */
export interface WaterfallDto {
  id?: string;
  name?: string;
  type?: string;
  lastExecuted?: Date | string;
  active?: boolean;
  updatedDate?: Date | string;
  updatedBy?: string;
}
